import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useOutletContext } from 'react-router-dom';
import { OnboardingProvider } from '@/contexts/OnboardingContext';
import './globals.css';
import { Loader2 } from 'lucide-react';
import { useToast } from './hooks/useToast';
import { UserProfile, canManageAllOrganisations, canManageCharities, canManageUsers, canViewReports, canPurchaseCredits, canManageOrgCharityConfig, canManageNumberRegistry } from './types/user';

const DashboardLayout = lazy(() => import('@/layouts/DashboardLayout'));
const Login = lazy(() => import('@/components/Login'));
const ForgotPassword = lazy(() => import('@/components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const LandingPage = lazy(() => import('@/components/LandingPage'));
const OnboardingPage = lazy(() => import('@/components/Onboarding'));
const SMSDashboard = lazy(() => import('@/components/SMSDashboard/SMSDashboard'));
const ManageCharities = lazy(() => import('@/components/SMSDashboard/ManageCharities'));
const ManageUsers = lazy(() => import('@/components/SMSDashboard/ManageUsers'));
const EditCharityPage = lazy(() => import('./components/SMSDashboard/charities/EditCharityPage'));
const AddCharityPage = lazy(() => import('./components/SMSDashboard/charities/AddCharityPage'));
const CharityConfigurationPage = lazy(() => import('./components/SMSDashboard/charities/CharityConfigurationPage'));
const ManageOrganisations = lazy(() => import('@/components/SMSDashboard/ManageOrganisations'));
const NumberRegistryPage = lazy(() => import('@/components/SMSDashboard/NumberRegistryPage'));
const UserGuide = lazy(() => import('@/components/UserGuide/UserGuide').then(m => ({ default: m.UserGuide })));
const ReportsPage = lazy(() => import('./components/ReportsPage'));
const PurchaseCreditsPage = lazy(() => import('./components/SMSDashboard/PurchaseCreditsPage'));
const BillingPage = lazy(() => import('@/components/SMSDashboard/BillingPage'));

const PageSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-10 w-10 animate-spin" />
  </div>
);

if (process.env.REACT_APP_ENV === "production") {
  console.log = () => {};
  console.debug = () => {};
  console.info = () => {};
  console.warn = () => {};
}

// Helper for public routes that redirect if authenticated
const AuthRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const isAuthenticated = !!localStorage.getItem('token');
  return !isAuthenticated ? children : <Navigate to="/dashboard" replace />;
};

// Helper for routes that require authentication
const AuthenticatedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
    const isAuthenticated = !!localStorage.getItem('token');
    return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// --- Component for checking role-based permissions ---
interface PermissionProtectedRouteProps {
  children: React.ReactElement;
  hasPermission: (profile: UserProfile) => boolean;
}

const PermissionProtectedRoute: React.FC<PermissionProtectedRouteProps> = ({ children, hasPermission }) => {
  const { userProfile } = useOutletContext<{ userProfile: UserProfile | null }>();
  const { showToast } = useToast();

  if (!userProfile) {
    return null;
  }

  if (!hasPermission(userProfile)) {
    showToast('error', 'Access Denied: You do not have permission to view this page.');
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const App: React.FC = () => {
  const isDevelopmentOrStaging = () => {
    const hostname = window.location.hostname;
    return hostname.includes('kylie-sms-development') || hostname.includes('kylie-sms-staging');
  };

  return (
    <Router>
      <div className="App">
        <Suspense fallback={<PageSpinner />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />

          {/* Onboarding Routes */}
          <Route path="/onboarding" element={ isDevelopmentOrStaging() ? <OnboardingProvider><OnboardingPage /></OnboardingProvider> : <Navigate to="/" /> } />
          <Route path="/onboarding/:token" element={<OnboardingProvider><OnboardingPage /></OnboardingProvider>} />

          <Route path="/guide/:accessKey" element={<UserGuide />} />

          {/* Auth routes */}
          <Route path="/login" element={<AuthRoute><Login /></AuthRoute>} />
          <Route path="/forgot-password" element={<AuthRoute><ForgotPassword /></AuthRoute>} />
          <Route path="/reset-password" element={<AuthRoute><ResetPassword /></AuthRoute>} />

          {/* Protected routes under DashboardLayout */}
          <Route
            element={
              <AuthenticatedRoute>
                <DashboardLayout />
              </AuthenticatedRoute>
            }
          >
            <Route path="/dashboard" element={<SMSDashboard />} />

            <Route path="/reports" element={
              <PermissionProtectedRoute hasPermission={canViewReports}>
                <ReportsPage />
              </PermissionProtectedRoute>
            } />

            <Route path="/manage-organisations" element={
              <PermissionProtectedRoute hasPermission={canManageAllOrganisations}>
                <ManageOrganisations />
              </PermissionProtectedRoute>
            } />

            <Route path="/number-registry" element={
              <PermissionProtectedRoute hasPermission={canManageNumberRegistry}>
                <NumberRegistryPage />
              </PermissionProtectedRoute>
            } />

            <Route path="/manage-charities" element={
              <PermissionProtectedRoute hasPermission={canManageCharities}>
                <ManageCharities />
              </PermissionProtectedRoute>
            } />
            <Route path="/manage-charities/add" element={
              <PermissionProtectedRoute hasPermission={canManageCharities}>
                <AddCharityPage />
              </PermissionProtectedRoute>
            } />
            <Route path="/manage-charities/edit/:id" element={
              <PermissionProtectedRoute hasPermission={canManageCharities}>
                <EditCharityPage />
              </PermissionProtectedRoute>
            } />
            <Route path="/manage-charities/config/:orgId/:charityId" element={
              <PermissionProtectedRoute hasPermission={canManageOrgCharityConfig}>
                <CharityConfigurationPage />
              </PermissionProtectedRoute>
            } />

            <Route path="/manage-users" element={
              <PermissionProtectedRoute hasPermission={canManageUsers}>
                <ManageUsers />
              </PermissionProtectedRoute>
            } />
            
            <Route path="/billing" element={
              <PermissionProtectedRoute hasPermission={canPurchaseCredits}>
                <BillingPage />
              </PermissionProtectedRoute>
            } />

            <Route path="/purchase-credits" element={
              <PermissionProtectedRoute hasPermission={canPurchaseCredits}>
                <PurchaseCreditsPage />
              </PermissionProtectedRoute>
            } />

          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </div>
    </Router>
  );
}

export default App;